import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Users, Loader2, X, Check, Globe2, Map, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useLang } from "@/context/LangContext";
import { API } from "@/lib/api";

const fmt = (n) => (n == null ? "—" : String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "."));
const TYPE = { city: [["Città", "City"], Building2], region: [["Regione / Cantone", "Region / Canton"], Map], country: [["Nazione", "Country"], Globe2] };

const useDebounced = (value, ms = 180) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

const useOutside = (ref, cb) => {
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && cb();
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [ref, cb]);
};

const Dropdown = ({ children, testid }) => (
  <div data-testid={testid} className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-[#cfd6d9] shadow-xl max-h-80 overflow-y-auto">
    {children}
  </div>
);

export const LocationAutocomplete = ({ value, onChange }) => {
  const { tr } = useLang();
  const [q, setQ] = useState(value?.label || "");
  const [res, setRes] = useState({ q: null, items: [] });
  const pending = res.q !== q;
  const items = pending ? [] : res.items;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const dq = useDebounced(q);
  useOutside(ref, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/suggest/locations`, { params: { q: dq } }).then((r) => { setRes({ q: dq, items: r.data.locations }); setActive(0); }).catch(() => {});
  }, [dq, open]);

  const pick = (loc) => { onChange(loc); setQ(loc.label); setOpen(false); };
  const custom = () => q.trim().length >= 2 && pick({ name: q.trim(), label: q.trim(), type: "custom", population: null });
  const commit = () => { if (value || q.trim().length < 2) return; const m = items.find((l) => l.name.toLowerCase() === q.trim().toLowerCase()); m ? pick(m) : custom(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); if (!pending) items[active] ? pick(items[active]) : custom(); }
  };

  return (
    <div ref={ref} className="relative">
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E0262C]" />
      <Input data-testid="geo-input" value={q} onFocus={() => setOpen(true)} onKeyDown={onKey} onBlur={commit}
        onChange={(e) => { setQ(e.target.value); setOpen(true); if (value) onChange(null); }}
        placeholder={tr("Scrivi una città, una regione o una nazione (es. Lugano, Canton Ticino, Italia)", "Type a city, region or country (e.g. Lugano, Ticino, Italy)")}
        className="pl-11 h-14 rounded-sm border-[#cfd6d9] bg-white text-base" />
      {value && <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10b981]" />}
      {open && (items.length > 0 || q.trim().length >= 2) && (
        <Dropdown testid="geo-suggestions">
          {items.map((l, i) => {
            const [[it, en], Icon] = TYPE[l.type];
            const label = tr(it, en);
            return (
              <button key={l.label} type="button" data-testid={`geo-option-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(l)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#f0f2f3] ${i === active ? "bg-[#fdecec]" : "hover:bg-[#f6f7f8]"}`}>
                <Icon className="w-4 h-4 text-[#5b6168]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{l.name}</div>
                  <div className="text-[11px] text-[#71717a]">{label} · {l.country_name}</div>
                </div>
                <span className="text-[11px] font-mono text-[#5b6168]">{fmt(l.population)} {tr("ab.", "pop.")}</span>
              </button>
            );
          })}
          {pending && <div data-testid="geo-loading" className="px-4 py-3 text-sm text-[#a1a1aa] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /></div>}
          {!pending && !items.length && (
            <button type="button" data-testid="geo-custom" onMouseDown={(e) => e.preventDefault()} onClick={custom}
              className="w-full text-left px-4 py-3 text-sm hover:bg-[#f6f7f8]">{tr("Usa", "Use")} “{q.trim()}”</button>
          )}
        </Dropdown>
      )}
    </div>
  );
};

export const SkillAutocomplete = ({ location, skills, onChange }) => {
  const { tr } = useLang();
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ q: null, items: [] });
  const pending = res.q !== q;
  const items = pending ? [] : res.items;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const dq = useDebounced(q);
  const disabled = !location;
  useOutside(ref, () => setOpen(false));

  useEffect(() => {
    if (!open || disabled) return;
    setLoading(true);
    axios.get(`${API}/suggest/skills`, { params: { q: dq, location: location.name } })
      .then((r) => { setRes({ q: dq, items: r.data.skills.filter((s) => !skills.some((x) => x.label === s.label)) }); setActive(0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [dq, open, disabled, location, skills]);

  const add = (s) => { onChange([...skills, s]); setQ(""); };
  const custom = () => q.trim().length >= 2 && add({ label: q.trim(), category: tr("Personalizzata", "Custom"), audience: null });
  const commit = () => { if (q.trim().length < 2) return; const m = items.find((s) => s.label.toLowerCase() === q.trim().toLowerCase()); m ? add(m) : custom(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); if (!pending) items[active] ? add(items[active]) : custom(); }
    if (e.key === "Backspace" && !q && skills.length) onChange(skills.slice(0, -1));
  };

  return (
    <div ref={ref} className="relative">
      <div className={`min-h-14 border rounded-sm bg-white flex flex-wrap items-center gap-2 px-3 py-2 ${disabled ? "border-dashed border-[#cfd6d9] opacity-60" : "border-[#cfd6d9]"}`}>
        {skills.map((s, i) => (
          <span key={s.label} data-testid={`skill-chip-${i}`} className="inline-flex items-center gap-2 bg-[#2B3036] text-white pl-3 pr-1.5 py-1.5 text-sm">
            {s.label}
            {s.audience != null && <span className="text-[10px] font-mono text-[#fca5a5]">~{fmt(s.audience)}</span>}
            <button type="button" data-testid={`skill-remove-${i}`} onClick={() => onChange(skills.filter((_, idx) => idx !== i))} className="hover:text-[#E0262C]"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        <input data-testid="skill-input" value={q} disabled={disabled} onFocus={() => setOpen(true)} onKeyDown={onKey} onBlur={commit}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={disabled ? tr("Prima scegli la location al passo 1", "First choose the location in step 1") : skills.length ? tr("Aggiungi un'altra skill...", "Add another skill...") : tr("Scrivi la skill (es. eventi, sale da ballo, ristoranti)", "Type the skill (e.g. events, dance halls, restaurants)")}
          className="flex-1 min-w-[200px] h-9 outline-none text-base bg-transparent placeholder:text-[#a1a1aa]" />
        {(loading || (open && pending)) && <Loader2 className="w-4 h-4 animate-spin text-[#a1a1aa]" />}
      </div>
      {open && !disabled && (items.length > 0 || q.trim().length >= 2) && (
        <Dropdown testid="skill-suggestions">
          <div className="px-4 py-2 text-[10px] uppercase tracking-widest font-mono text-[#71717a] bg-[#f6f7f8]">{tr("Persone potenzialmente interessate a", "People potentially interested in")} {location.name}</div>
          {items.map((s, i) => (
            <button key={s.label} type="button" data-testid={`skill-option-${i}`} onMouseDown={(e) => e.preventDefault()} onClick={() => add(s)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[#f0f2f3] ${i === active ? "bg-[#fdecec]" : "hover:bg-[#f6f7f8]"}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{s.label}</div>
                <div className="text-[11px] text-[#71717a]">{s.category}</div>
              </div>
              {s.audience != null && (
                <span className="inline-flex items-center gap-1.5 text-sm font-display text-[#E0262C]"><Users className="w-3.5 h-3.5" /> ~{fmt(s.audience)}</span>
              )}
            </button>
          ))}
          {!pending && q.trim().length >= 2 && !items.some((s) => s.label.toLowerCase() === q.trim().toLowerCase()) && (
            <button type="button" data-testid="skill-custom" onMouseDown={(e) => e.preventDefault()} onClick={custom}
              className="w-full text-left px-4 py-3 text-sm hover:bg-[#f6f7f8]">{tr("Aggiungi come skill personalizzata:", "Add as a custom skill:")} “{q.trim()}”</button>
          )}
        </Dropdown>
      )}
    </div>
  );
};

export const AudienceMeter = ({ location, skills }) => {
  const { tr } = useLang();
  const known = skills.filter((s) => s.audience != null);
  const total = known.reduce((a, s) => a + s.audience, 0);
  if (!location || !skills.length) return null;
  return (
    <div data-testid="audience-meter" className="mt-4 border border-[#cfd6d9] bg-white p-5 flex flex-wrap items-end gap-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-mono text-[#71717a] mb-1">{tr("Pubblico potenziale stimato", "Estimated potential audience")} · {location.name}</div>
        <div data-testid="audience-total" className="font-display text-5xl leading-none text-[#E0262C]">{known.length ? `~${fmt(total)}` : "—"}</div>
      </div>
      <div className="text-xs text-[#5b6168] max-w-sm pb-1">
        {tr("Persone potenzialmente interessate", "People potentially interested")} {known.length ? `${tr("a", "in")} ${known.map((s) => s.label).join(", ")}` : ""}. {tr("Stima basata su popolazione e interessi medi dell'area: i contatti reali vengono raccolti durante la campagna.", "Estimate based on the area's population and average interests: real contacts are collected during the campaign.")}
      </div>
    </div>
  );
};
